import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { getSignedReportAssetUrl } from '../../utils/api/reportApi';
import styles from './TargetAudienceViewer.module.css';

const getImageUrl = (image = {}) =>
  image?.url || image?.imageUrl || null;

const TargetAudienceViewer = ({ data = {} }) => {
  const { user } = useAuthUser();
  const authToken = user?.token;
  const payload = data?.data || data;
  const summary =
    typeof payload?.summary === 'string' ? payload.summary.trim() : '';
  const profiles = Array.isArray(payload?.icp_profiles) ? payload.icp_profiles : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [refreshedImageUrls, setRefreshedImageUrls] = useState({});
  const [refreshingKeys, setRefreshingKeys] = useState({});
  const activeProfile = profiles[activeIndex] || {};
  const imageUrl = useMemo(() => {
    const key = activeProfile?.image?.key;
    if (key && refreshedImageUrls[key]) return refreshedImageUrls[key];
    return getImageUrl(activeProfile?.image);
  }, [activeProfile?.image, refreshedImageUrls]);

  const refreshSignedImageUrl = useCallback(
    async (image = {}) => {
      const key = image?.key;
      if (!key || refreshingKeys[key]) return null;

      setRefreshingKeys((current) => ({ ...current, [key]: true }));
      try {
        const response = await getSignedReportAssetUrl({ key, authToken });
        const signedUrl = response?.url ? String(response.url).trim() : '';
        if (!signedUrl) return null;
        setRefreshedImageUrls((current) => ({ ...current, [key]: signedUrl }));
        return signedUrl;
      } catch (error) {
        console.error('[TargetAudienceViewer] failed to refresh signed image url', {
          key,
          error: error?.message || String(error),
        });
        return null;
      } finally {
        setRefreshingKeys((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      }
    },
    [authToken, refreshingKeys]
  );

  useEffect(() => {
    if (!profiles.length) return;
    profiles.forEach((profile) => {
      const url = getImageUrl(profile?.image);
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [profiles]);

  useEffect(() => {
    if (imageUrl || !activeProfile?.image?.key) return;
    refreshSignedImageUrl(activeProfile.image);
  }, [activeProfile, imageUrl, refreshSignedImageUrl]);

  const arrays = useMemo(
    () => ({
      likes: Array.isArray(activeProfile?.likes) ? activeProfile.likes : [],
      dislikes: Array.isArray(activeProfile?.dislikes) ? activeProfile.dislikes : [],
      platforms: Array.isArray(activeProfile?.platforms) ? activeProfile.platforms : [],
      forums: Array.isArray(activeProfile?.forums) ? activeProfile.forums : [],
      social: Array.isArray(activeProfile?.social_media) ? activeProfile.social_media : [],
      marketplaces: Array.isArray(activeProfile?.marketplaces)
        ? activeProfile.marketplaces
        : [],
      offline: Array.isArray(activeProfile?.offline_activities)
        ? activeProfile.offline_activities
        : [],
      pain: Array.isArray(activeProfile?.pain_points_transactional)
        ? activeProfile.pain_points_transactional
        : [],
      usp: Array.isArray(activeProfile?.usp) ? activeProfile.usp : [],
      signals: Array.isArray(activeProfile?.behavioral_signals)
        ? activeProfile.behavioral_signals
        : [],
      interests: Array.isArray(activeProfile?.interest_graphs)
        ? activeProfile.interest_graphs
        : [],
    }),
    [activeProfile]
  );

  const countStats = useMemo(
    () => [
      { label: 'USPs', value: arrays.usp.length },
      { label: 'Pain Points', value: arrays.pain.length },
      { label: 'Signals', value: arrays.signals.length },
      { label: 'Platforms', value: arrays.platforms.length },
    ],
    [arrays]
  );

  return (
    <div className={styles.container}>
      {summary ? (
        <div className={styles.summaryCard}>
          <p className={styles.summaryLabel}>Report Summary</p>
          <p className={styles.summaryText}>{summary}</p>
        </div>
      ) : null}
      {/* ICP Tabs */}
      {profiles.length > 1 ? (
        <div className={styles.icpTabs}>
          {profiles.map((profile, idx) => {
            const active = idx === activeIndex;
            const label = profile?.title || `ICP ${idx + 1}`;
            return (
              <button
                key={`${label}-${idx}`}
                className={`${styles.icpTab} ${active ? styles.icpTabActive : ''}`}
                onClick={() => setActiveIndex(idx)}
              >
                <span className={`${styles.icpTabText} ${active ? styles.icpTabTextActive : ''}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Hero Section */}
      <div className={styles.hero}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="ICP"
            className={styles.heroImage}
            onError={() => {
              refreshSignedImageUrl(activeProfile?.image);
            }}
          />
        ) : (
          <div className={styles.heroFallback} />
        )}

        <div className={styles.heroCard}>
          <div className={styles.pillRow}>
            <span className={styles.pill}>
              <span className={styles.pillText}>
                {activeProfile?.title || 'ICP 1 (Core / Revenue ICP)'}
              </span>
            </span>
            <span className={`${styles.pill} ${styles.pillAlt}`}>
              <span className={styles.pillTextAlt}>
                {Math.round((activeProfile?.focus_share || 0) * 100)}% focus
              </span>
            </span>
          </div>
          <h2 className={styles.heroTitle}>Target Audience Profile</h2>
          <p className={styles.heroParagraph}>
            {activeProfile?.profile_brief ||
              'Profile brief pending. Generate the target audience report to see the ICP narrative.'}
          </p>
          <div className={styles.heroStats}>
            {countStats.map((stat) => (
              <div key={stat.label} className={styles.stat}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bento Grid */}
      <div className={styles.bentoGrid}>
        <div className={`${styles.bentoCard} ${styles.bentoWide}`}>
          <h4 className={styles.cardTitle}>Snapshot</h4>
          <div className={styles.snapshotRow}>
            <SnapshotItem label="Country" value={activeProfile?.country} />
            <SnapshotItem label="City" value={activeProfile?.city} />
          </div>
          <div className={styles.snapshotRow}>
            <SnapshotItem label="Gender" value={activeProfile?.gender} />
            <SnapshotItem label="Age Range" value={activeProfile?.age_range} />
          </div>
          <div className={styles.snapshotRow}>
            <SnapshotItem label="Income" value={activeProfile?.household_income} />
            <SnapshotItem label="Niche" value={activeProfile?.niche} />
          </div>
        </div>

        <div className={styles.bentoCard}>
          <h4 className={styles.cardTitle}>Brand Positioning</h4>
          <p className={styles.cardBody}>
            {activeProfile?.brand_positioning || 'Not available'}
          </p>
        </div>

        <div className={styles.bentoCard}>
          <h4 className={styles.cardTitle}>Purchase Behavior</h4>
          <p className={styles.cardBody}>
            {activeProfile?.purchase_behavior || 'Not available'}
          </p>
        </div>
      </div>

      {/* Deep Dives Section */}
      <h3 className={styles.sectionHeader}>Deep Dives</h3>
      <div className={styles.slideRow}>
        <Slide title="Likes">
          <ListItems items={arrays.likes} emptyText="No likes yet." />
        </Slide>
        <Slide title="Dislikes">
          <ListItems items={arrays.dislikes} emptyText="No dislikes yet." />
        </Slide>
        <Slide title="Pain Points">
          <ListItems items={arrays.pain} emptyText="No transactional pain points yet." />
        </Slide>
        <Slide title="USPs">
          <ListItems items={arrays.usp} emptyText="No USPs yet." />
        </Slide>
        <Slide title="Behavioral Signals">
          <ListItems items={arrays.signals} emptyText="No signals yet." />
        </Slide>
        <Slide title="Interest Graphs">
          <ListItems items={arrays.interests} emptyText="No interest graphs yet." />
        </Slide>
        <Slide title="Platforms">
          <ListItems items={arrays.platforms} emptyText="No platforms yet." />
        </Slide>
        <Slide title="Social Media">
          <ListItems items={arrays.social} emptyText="No social media yet." />
        </Slide>
        <Slide title="Forums">
          <ListItems items={arrays.forums} emptyText="No forums yet." />
        </Slide>
        <Slide title="Marketplaces">
          <ListItems items={arrays.marketplaces} emptyText="No marketplaces yet." />
        </Slide>
        <Slide title="Offline Activities">
          <ListItems items={arrays.offline} emptyText="No offline activities yet." />
        </Slide>
      </div>
    </div>
  );
};

const SnapshotItem = ({ label, value }) => (
  <div className={styles.snapshotItem}>
    <span className={styles.snapshotLabel}>{label}</span>
    <span className={styles.snapshotValue}>{value || '-'}</span>
  </div>
);

const Slide = ({ title, children }) => (
  <div className={styles.slide}>
    <h5 className={styles.slideTitle}>{title}</h5>
    {children}
  </div>
);

const ListItems = ({ items, emptyText }) => {
  if (!Array.isArray(items) || !items.length) {
    return <p className={styles.emptyText}>{emptyText}</p>;
  }
  return (
    <div className={styles.list}>
      {items.map((item, idx) => (
        <div key={`${item}-${idx}`} className={styles.listRow}>
          <div className={styles.dot} />
          <p className={styles.listText}>{item}</p>
        </div>
      ))}
    </div>
  );
};

export default TargetAudienceViewer;
